import { useEffect, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import useConstCallback from './useConstCallback'

const CLOSE_ON_ESCAPE_MAP: Map<string, () => void> = new Map()

export default function useOnEscape(callback: () => void, isActive = true) {
  const uuidRef = useRef(uuidv4())

  const handleKeyboard = useConstCallback(event => {
    if (event.metaKey || event.altKey || event.ctrlKey) {
      return
    }

    if (event.key === 'Escape') {
      const latestUuid = Array.from(CLOSE_ON_ESCAPE_MAP).pop()?.[0]

      if (latestUuid) {
        const latestCallback = CLOSE_ON_ESCAPE_MAP.get(latestUuid)

        latestCallback?.()
      }
    }
  }, [])

  const handleMount = useConstCallback(
    (uuid: string) => {
      if (!CLOSE_ON_ESCAPE_MAP.size) {
        window.addEventListener('keydown', handleKeyboard)
      }

      CLOSE_ON_ESCAPE_MAP.set(uuid, callback)
    },
    [callback, handleKeyboard],
  )

  const handleUnmount = useConstCallback(
    (uuid: string) => {
      CLOSE_ON_ESCAPE_MAP.delete(uuid)

      if (!CLOSE_ON_ESCAPE_MAP.size) {
        window.removeEventListener('keydown', handleKeyboard)
      }
    },
    [handleKeyboard],
  )

  useEffect(() => {
    if (isActive) {
      const uuid = uuidRef.current

      handleMount(uuid)

      return () => {
        handleUnmount(uuid)
      }
    }

    return undefined
  }, [isActive, callback])
}
